import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { HttpStatusCode } from '../assets/enums/index.ts';

import { Mutex } from 'async-mutex'
import { API } from './api.ts';
import { emitApiErrorPageEvent, shouldDisplayApiErrorPage } from './response-errors.ts';
const mutex = new Mutex()

export const instanceCore = axios.create({
  baseURL: '/api/',
  withCredentials: true,
});

instanceCore.interceptors.request.use(config => {
  const token = localStorage.getItem('access_token');

  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }

  return config;
});

instanceCore.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {

    if (error.response?.status === HttpStatusCode.UNAUTHORIZED_401) {
      localStorage.removeItem('access_token');
    }

    if (shouldDisplayApiErrorPage(error)) {
      emitApiErrorPageEvent(error);
    }

    return Promise.reject(error)
  }
)

instanceCore.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // only 401 error && not a retry request && not update-tokens request
    if (
      error.response?.status === HttpStatusCode.UNAUTHORIZED_401 &&
      !originalRequest._retry &&
      originalRequest.url !== '/auth/refresh'
    ) {
      // Wait if the mutex is locked (another request is refreshing the token)
      await mutex.waitForUnlock()

      // Check whether the mutex is locked
      if (!mutex.isLocked()) {
        // Lock the mutex
        const release = await mutex.acquire()

        try {
          // Send a request to refresh the token
          const { access_token, refresh_token } = await API.auth.refresh()

          if (access_token && refresh_token) {
            localStorage.setItem('access_token', access_token)
            localStorage.setItem('refresh_token', refresh_token)
            // Mark request as retried
            originalRequest._retry = true
            // Retry the original request
            return instanceCore(originalRequest)
          }
        } finally {
          // Release the mutex
          release()
        }
      } else {
        // If the mutex is locked, wait for it to be released and retry the request
        await mutex.waitForUnlock()
        return instanceCore(originalRequest)
      }
    }

    return Promise.reject(error)
  }
)

